import * as React from "react";
import Link from "next/link";
import { ChevronLeft, UserRound } from "lucide-react";
import { cn } from "@gerama/utils";
import { MediaImage as Image } from "@/components/media-image";
import { artistPath } from "@/features/artists/artist-path";

/** A titled block of a browse page with a "see all" link. */
export function Shelf({
  title,
  href,
  seeAllLabel = "مشاهدهٔ همه",
  children,
  className,
}: {
  title: React.ReactNode;
  href?: string;
  seeAllLabel?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("space-y-4", className)}>
      <div className="flex items-end justify-between gap-4">
        <h2 className="text-xl font-bold sm:text-2xl">{title}</h2>
        {href && (
          <Link
            href={href}
            className="text-muted-foreground hover:text-foreground flex shrink-0 items-center text-xs font-bold tracking-wide"
          >
            {seeAllLabel}
            <ChevronLeft className="size-4" />
          </Link>
        )}
      </div>
      {children}
    </section>
  );
}

/** Cover + title + subtitle — the card every shelf uses (albums, playlists, shows, videos). */
function MediaCardImpl({
  href,
  image,
  title,
  subtitle,
  aspect = "square",
  fallbackIcon,
  className,
  badge,
  priority,
}: {
  href: string;
  image: string | null;
  title: string;
  subtitle?: React.ReactNode;
  aspect?: "square" | "video";
  fallbackIcon?: React.ReactNode;
  className?: string;
  badge?: React.ReactNode;
  /** Above the fold: skip lazy loading (LCP). */
  priority?: boolean;
}) {
  return (
    <Link href={href} className={cn("group block min-w-0", className)}>
      <div
        className={cn(
          "bg-muted relative overflow-hidden rounded-lg shadow-md shadow-black/30",
          aspect === "video" ? "aspect-video" : "aspect-square",
        )}
      >
        {image ? (
          <Image
            src={image}
            alt=""
            fill
            sizes="(min-width: 1024px) 20vw, 45vw"
            priority={priority}
            className="object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <span className="text-muted-foreground absolute inset-0 flex items-center justify-center">
            {fallbackIcon}
          </span>
        )}
        {badge}
      </div>
      <p className="mt-2 truncate text-sm font-semibold">{title}</p>
      {subtitle && <p className="text-muted-foreground truncate text-xs">{subtitle}</p>}
    </Link>
  );
}

/** The round artist card. */
function ArtistCardImpl({
  id,
  code,
  name,
  image,
  subtitle,
  priority,
  compact,
}: {
  id: number;
  code?: string | null;
  name: string;
  image: string | null;
  subtitle?: React.ReactNode;
  /** Above the fold: skip lazy loading (LCP). */
  priority?: boolean;
  /** Smaller portrait — the full artist index, not the home shelves. */
  compact?: boolean;
}) {
  return (
    <Link href={artistPath({ id, code })} className="group block min-w-0 text-center">
      <div
        className={cn(
          "bg-muted relative mx-auto aspect-square overflow-hidden rounded-full shadow-md shadow-black/30",
          compact ? "w-20 sm:w-24 lg:w-28" : "w-full",
        )}
      >
        {image ? (
          <Image
            src={image}
            alt={name}
            fill
            sizes={compact ? "112px" : "(min-width: 1024px) 16vw, 30vw"}
            priority={priority}
            className="object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <UserRound className="text-muted-foreground absolute inset-0 m-auto size-1/3" />
        )}
      </div>
      <p className="mt-2 truncate text-sm font-semibold">{name}</p>
      {subtitle && <p className="text-muted-foreground truncate text-xs">{subtitle}</p>}
    </Link>
  );
}

/** Responsive card grid used by shelves and "see all" pages. */
export function CardGrid({
  children,
  className,
  columns = "default",
}: {
  children: React.ReactNode;
  className?: string;
  columns?: "default" | "video" | "artist";
}) {
  return (
    <div
      className={cn(
        "grid gap-4",
        columns === "default" && "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5",
        columns === "video" && "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
        columns === "artist" && "grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6",
        className,
      )}
    >
      {children}
    </div>
  );
}

/** Memoised: a list re-renders when a page is appended, not every row with it. */
export const MediaCard = React.memo(MediaCardImpl);

/** Memoised: a list re-renders when a page is appended, not every row with it. */
export const ArtistCard = React.memo(ArtistCardImpl);
